import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, LogOut, PackageCheck } from "lucide-react";
import api, { formatApiErrorDetail, gbp, imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthPanel from "@/components/AuthPanel";
import Footer from "@/components/Footer";
import { T } from "@/constants/testIds";

const STATUS_STYLES = {
  pending_payment: "bg-stone-900/10 text-stone-700 border-stone-400",
  paid: "bg-[#F5C518]/20 text-[#C79500] border-[#C79500]/50",
  ordered: "bg-blue-500/20 text-blue-700 border-blue-400/50",
  shipped: "bg-[#E52E39]/20 text-[#E52E39] border-[#E52E39]/50",
  delivered: "bg-green-500/20 text-green-700 border-green-400/50",
  refunded: "bg-violet-500/15 text-violet-700 border-violet-400/50",
  cancelled: "bg-red-100 text-red-700 border-red-700/60",
};

const COUNTRIES = ["United Kingdom", "Isle of Man", "Ireland", "France", "Germany", "Spain", "Netherlands", "Other (EU)"];

export default function Account() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [address, setAddress] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && user.id) {
      api
        .get("/orders/mine")
        .then((r) => setOrders(r.data || []))
        .catch(() => setOrders([]));
      if (address === null) {
        setAddress({
          name: user.name || "",
          address_line: user.default_address?.address_line || "",
          city: user.default_address?.city || "",
          postcode: user.default_address?.postcode || "",
          country: user.default_address?.country || "United Kingdom",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (user === null)
    return <div className="min-h-screen bg-[#FAF8F4]" />;

  if (!user)
    return (
      <div className="min-h-screen">
        <AuthPanel title="Your V.I.A account" subtitle="Sign in — or create an account in 20 seconds." />
        <Footer />
      </div>
    );

  const addr = address || {
    name: user.name || "",
    address_line: "",
    city: "",
    postcode: "",
    country: "United Kingdom",
  };
  const set = (k) => (e) => setAddress({ ...addr, [k]: e.target.value });

  const saveAddress = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/auth/me", { default_address: addr });
      toast.success("Delivery details saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save details");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="mx-auto max-w-7xl px-4 pb-20 pt-32 sm:px-8 lg:px-16">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="via-label">Signed in as {user.email}</p>
            <h1 className="font-display text-4xl font-black uppercase tracking-tight text-stone-950 sm:text-5xl">
              Hi {user.name}
            </h1>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-2 border border-stone-300 px-4 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-700 hover:border-[#E52E39] hover:text-[#E52E39]"
          >
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>

        <div className="mt-8 grid gap-4 lg:grid-cols-3">
          {/* Credit */}
          <div data-testid={T.accountCredit} className="border border-[#C79500]/50 bg-[#FFFBEF] p-6">
            <p className="via-label">Store credit</p>
            <p className="font-display text-4xl font-black text-[#C79500]">{gbp(user.credit_balance || 0)}</p>
            <p className="mt-2 text-xs leading-relaxed text-stone-500">
              If something's out of stock or not right, we can refund you straight into this balance — it's applied
              automatically at checkout.
            </p>
          </div>

          {/* Saved delivery details */}
          <form onSubmit={saveAddress} className="border border-stone-200 bg-white p-6 lg:col-span-2">
            <p className="via-label">Saved delivery details</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <p className="via-label">Recipient name</p>
                <input data-testid="account-address-name" className="via-input" value={addr.name} onChange={set("name")} />
              </div>
              <div>
                <p className="via-label">Address</p>
                <input data-testid="account-address-line" className="via-input" value={addr.address_line} onChange={set("address_line")} placeholder="Street, house / flat" />
              </div>
              <div>
                <p className="via-label">Town / city</p>
                <input data-testid="account-address-city" className="via-input" value={addr.city} onChange={set("city")} />
              </div>
              <div>
                <p className="via-label">Postcode</p>
                <input data-testid="account-address-postcode" className="via-input" value={addr.postcode} onChange={set("postcode")} />
              </div>
              <div>
                <p className="via-label">Country</p>
                <select data-testid="account-address-country" className="via-input" value={addr.country} onChange={set("country")}>
                  {COUNTRIES.map((c) => (
                    <option key={c} className="bg-white">{c}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  data-testid={T.accountSaveAddress}
                  type="submit"
                  disabled={saving}
                  className="w-full bg-stone-950 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-white hover:bg-[#E52E39] disabled:opacity-60"
                >
                  {saving ? "Saving…" : "Save details"}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Order history */}
        <h2 className="mb-4 mt-12 font-display text-2xl font-extrabold uppercase tracking-tight text-stone-950">
          Your orders ({orders.length})
        </h2>
        {orders.length === 0 ? (
          <div className="border border-stone-200 bg-white p-12 text-center">
            <PackageCheck className="mx-auto h-8 w-8 text-[#C79500]" />
            <p className="mt-4 font-mono text-sm uppercase tracking-widest text-stone-500">
              No orders yet — your history will appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((o) => (
              <div key={o.id} data-testid="account-order-card" className="border border-stone-200 bg-white p-5">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-200 pb-3">
                  <div>
                    <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-stone-950">
                      #{o.id.slice(0, 8).toUpperCase()}
                    </p>
                    <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-stone-400">
                      {new Date(o.created_at).toLocaleDateString("en-GB")} • {gbp(o.total)} •{" "}
                      {o.payment_method === "credit" ? "paid with credit" : "paid by card"}
                    </p>
                  </div>
                  <span className={`border px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-widest ${STATUS_STYLES[o.status] || ""}`}>
                    {o.status.replace("_", " ")}
                  </span>
                </div>
                <ul className="mt-3 space-y-1.5">
                  {o.items.map((it, i) => (
                    <li key={i} className="flex items-center justify-between gap-3 text-sm text-stone-700">
                      <span>
                        {it.name} <span className="font-mono text-[10px] uppercase tracking-widest text-[#C79500]">• size {it.size} × {it.qty}</span>
                      </span>
                      <span className="font-mono text-xs text-stone-500">{it.delivery_time}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
